/**
 * =============================================================================
 * ContentImageViewer.tsx
 * -----------------------------------------------------------------------------
 * Viewer globale che intercetta i click sulle immagini dei contenuti
 * editoriali (`.content-styled img`, `.prose img`, `.infographic img`,
 * `figure.content-figure img`) e:
 *
 *  - se l'immagine NON è dentro un link (`<a href>`): apre direttamente un
 *    lightbox con zoom/pan interattivo (libreria `react-zoom-pan-pinch`);
 *  - se l'immagine È dentro un link: mostra un dialog di scelta con
 *    "Mostra a dimensioni originali", "Vai alla risorsa collegata",
 *    "Apri in nuova scheda", "Copia URL risorsa".
 *
 * Il lightbox si chiude con la X, con Esc oppure cliccando l'overlay esterno.
 *
 * SCOPE — ESCLUSIONI:
 * - immagini dentro l'editor admin (`.ProseMirror`, `[contenteditable]`,
 *   `[data-editor-root]`) non devono attivare il viewer;
 * - immagini con `data-no-zoom` o dentro elementi con `data-no-zoom` vengono
 *   ignorate (utile per loghi/avatar in contenuti editoriali);
 * - immagini che usano il placeholder di fallback vengono ignorate.
 *
 * Il componente è montato una sola volta in `App.tsx`.
 * =============================================================================
 */

import { useCallback, useEffect, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { ExternalLink, Copy, Maximize2, X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Selettore degli scope considerati "contenuti editoriali". */
const CONTENT_SCOPES = [
  ".content-styled",
  ".prose",
  ".infographic",
  "figure.content-figure",
].join(",");

/** Selettore degli scope da escludere (editor admin, contenuti editabili). */
const EXCLUDED_SCOPES = [
  ".ProseMirror",
  "[contenteditable=\"true\"]",
  "[data-editor-root]",
  "[data-no-zoom]",
].join(",");

interface ChoiceState {
  src: string;
  alt: string;
  href: string;
}

interface ZoomState {
  src: string;
  alt: string;
}

export function ContentImageViewer() {
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const [choice, setChoice] = useState<ChoiceState | null>(null);

  // Gestore globale: delegation sui click. Filtra in base agli scope ammessi
  // e a quelli esclusi, poi mostra il viewer adatto.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // Ignora click con tasti modificatori: l'utente potrebbe voler
      // aprire la risorsa in una nuova scheda con Ctrl/Cmd+click.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;

      const target = event.target as Element | null;
      if (!target) return;
      const img = target.closest("img") as HTMLImageElement | null;
      if (!img) return;

      // Esclusioni esplicite
      if (img.closest(EXCLUDED_SCOPES)) return;
      if (img.hasAttribute("data-no-zoom")) return;
      // Non aprire il viewer su un placeholder (immagine non caricata)
      if (img.getAttribute("src")?.endsWith("/placeholder.svg")) return;

      // Deve trovarsi in uno scope editoriale
      if (!img.closest(CONTENT_SCOPES)) return;

      const link = img.closest("a") as HTMLAnchorElement | null;
      const src = img.currentSrc || img.src;
      const alt = img.alt || "";

      if (link && link.href) {
        // Immagine con link: previene la navigazione e mostra la scelta
        event.preventDefault();
        event.stopPropagation();
        setChoice({ src, alt, href: link.href });
      } else {
        // Immagine semplice: apre direttamente il lightbox
        event.preventDefault();
        event.stopPropagation();
        setZoom({ src, alt });
      }
    };

    // Capture phase per intercettare il click prima di eventuali handler React.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // Copia l'URL della risorsa collegata negli appunti.
  const copyHref = useCallback(async (href: string) => {
    try {
      await navigator.clipboard.writeText(href);
      toast.success("URL copiato negli appunti");
    } catch {
      toast.error("Copia non riuscita");
    }
  }, []);

  return (
    <>
      {/* ============================================================
          Dialog di scelta per immagini con link
          ============================================================ */}
      <Dialog open={!!choice} onOpenChange={(open) => !open && setChoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Immagine collegata a una risorsa</DialogTitle>
            <DialogDescription className="break-all">
              {choice?.href}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant="default"
              className="justify-start"
              onClick={() => {
                if (!choice) return;
                const next = { src: choice.src, alt: choice.alt };
                setChoice(null);
                setZoom(next);
              }}
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              Mostra a dimensioni originali
            </Button>
            <Button
              variant="secondary"
              className="justify-start"
              onClick={() => {
                if (!choice) return;
                window.location.href = choice.href;
                setChoice(null);
              }}
            >
              Vai alla risorsa collegata
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => {
                if (!choice) return;
                window.open(choice.href, "_blank", "noopener,noreferrer");
                setChoice(null);
              }}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Apri in nuova scheda
            </Button>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => {
                if (!choice) return;
                void copyHref(choice.href);
              }}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copia URL risorsa
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          Lightbox con zoom/pan interattivo
          - Esc / click sull'overlay / X chiudono il viewer (gestiti da
            Radix Dialog).
          - Doppio click o pinch-zoom per ingrandire, drag per spostarsi.
          ============================================================ */}
      <Dialog open={!!zoom} onOpenChange={(open) => !open && setZoom(null)}>
        <DialogContent
          className="max-w-[95vw] w-[95vw] h-[92vh] p-0 bg-black/95 border-none flex flex-col overflow-hidden"
          aria-label={zoom?.alt || "Anteprima immagine"}
        >
          {/* Header invisibile per accessibilità (DialogTitle obbligatorio) */}
          <DialogHeader className="sr-only">
            <DialogTitle>{zoom?.alt || "Immagine"}</DialogTitle>
            <DialogDescription>
              Usa la rotellina o il pinch per ingrandire, trascina per
              spostarti. Premi Esc per chiudere.
            </DialogDescription>
          </DialogHeader>

          {/* Pulsante chiusura sempre visibile in alto a destra. */}
          <DialogClose asChild>
            <button
              type="button"
              className="absolute right-3 top-3 z-10 rounded-full bg-background/80 hover:bg-background p-2 text-foreground shadow"
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogClose>

          {zoom && (
            <TransformWrapper
              // Limiti generosi per consentire ingrandimenti spinti su
              // dettagli, ma senza permettere rimpicciolimenti oltre il fit.
              minScale={1}
              maxScale={8}
              initialScale={1}
              centerOnInit
              doubleClick={{ mode: "toggle", step: 2 }}
              wheel={{ step: 0.15 }}
              pinch={{ step: 5 }}
            >
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={zoom.src}
                  alt={zoom.alt}
                  // Mostra l'immagine nelle sue dimensioni naturali, ma se
                  // più grande del viewport viene rimpicciolita per starci
                  // dentro (poi l'utente può fare zoom).
                  style={{
                    maxWidth: "95vw",
                    maxHeight: "88vh",
                    objectFit: "contain",
                    userSelect: "none",
                    WebkitUserDrag: "none",
                  } as React.CSSProperties}
                  draggable={false}
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ContentImageViewer;